import React, { useRef, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import type { GridReadyEvent, RowNode, ColDef } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { Button, Pagination, Row, Space, Spin, Empty, Tooltip } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import styles from './SimpleAgGrid.module.css';

interface SimpleAgGridProps<T extends Record<string, any>> {
  page?: any;
  dataSource?: any
  className?: any
  column: any;
  rowSelection?: any;
  rowKey?: any;
  onRowDoubleClick?: (event: any) => void;
  onCellClicked?: (event: any) => void;
  pageSizeOptions?: any
  defaultPageSize?: number
  showQuickJumper?: boolean
  showSizeChanger?: boolean
  pagination?: boolean;
  pinnedBottomRowData?: T[];
  getRowStyle?: (params: { node: RowNode }) => React.CSSProperties;
  onChangePage?: (current: number, size?: number) => void
  onRowSelected?: (selected?: boolean, node?: RowNode) => void; // 新增行选中回调
  onSelectionChange?: (selectedRows: T[], selectedNodes: RowNode[], selectedRowIds: any[]) => void; // 新增选择变化回调
  rowHeight?: number;
  headerHeight?: number;
  isLoading?: boolean;
  suppressRowClickSelection?: boolean;
  rowMultiSelectWithClick?: boolean;
  pageKey?: string;
  onColumnResized?: (event: any) => void; // 新增列宽变化回调
  onColumnMoved?: (columnOrder: string[]) => void; // 新增列移动回调
  onSortChanged?: (sortModel: any[], hasComparator: boolean) => void; // 新增排序变化回调
  selectedRowKeys?: any[]; // 跨页选中的行ID列表
  getRowId?: (data: any) => any; // 获取行唯一ID的函数
  sortedInfo?: any; // 排序信息
}

const SimpleAgGrid = <T extends Record<string, any>>({
  page,
  dataSource,
  className,
  column,
  pageSizeOptions,
  rowSelection,
  rowKey,
  pinnedBottomRowData,
  onRowDoubleClick,
  onCellClicked,
  pagination = true,
  defaultPageSize,
  showQuickJumper = true,
  showSizeChanger = true,
  getRowStyle,
  onChangePage,
  onSelectionChange,
  onRowSelected,
  headerHeight = 41,
  rowHeight = 41,
  isLoading = false,
  suppressRowClickSelection = false,
  rowMultiSelectWithClick,
  pageKey = '',
  onColumnResized,
  onColumnMoved,
  onSortChanged,
  selectedRowKeys,
  getRowId,
  sortedInfo,
}: SimpleAgGridProps<T>) => {
  const gridRef: any = useRef<AgGridReactType>(null);

  // 用于防抖处理onSelectionChanged事件
  const selectionDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isColumnMovedRef = useRef(false);

  // 选中行的函数
  const selectRowsBasedOnKeys = useCallback((params: any) => {
    if (selectedRowKeys && selectedRowKeys.length > 0 && getRowId) {
      setTimeout(() => {
        params.api.forEachNode((node: any) => {
          const rowId = getRowId(node.data);
          if (selectedRowKeys.includes(rowId)) {
            node.setSelected(true);
          }
        });
      }, 100);
    }
  }, [selectedRowKeys, getRowId]);

  useEffect(() => {
    if (!selectedRowKeys?.length) {
      // 当selectedRowKeys为空时，清除表格中的所有勾选项
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.deselectAll();
      }
    }
  }, []);

  // 清理防抖定时器
  useEffect(() => {
    return () => {
      if (selectionDebounceRef.current) {
        clearTimeout(selectionDebounceRef.current);
      }
    };
  }, []);

  // 自定义列标题组件，支持Ant Design风格的排序图标
  const CustomHeaderComponent = useCallback((props: any) => {
    const { displayName, column, api, columnApi, promoteSort } = props;

    // 获取列定义
    const colDef = column.getColDef();

    // 检查列是否可排序
    const isSortable = colDef.sortable !== false;

    // 检查是否有自定义比较函数（前端排序）
    const hasComparator = colDef.comparator && typeof colDef.comparator === 'function';

    // 获取当前排序状态
    const currentSort = column.getSort();

    // 点击列标题触发排序
    const handleSortClick = (event: React.MouseEvent) => {
      if (!isSortable) return;

      // 循环排序：none -> asc -> desc -> none
      let nextSort: any
      if (!currentSort) {
        nextSort = 'asc';
      } else if (currentSort === 'asc') {
        nextSort = 'desc';
      } else {
        nextSort = null;
      }

      // 使用 columnApi 获取所有列并重置其他列的排序状态
      if (columnApi) {
        const allColumns = columnApi?.getAllColumns();
        if (allColumns) {
          allColumns.forEach((col: any) => {
            if (col.getColId() !== column.getColId()) {
              col.setSort(null);
            }
          });
        }
      }

      // 应用排序
      column.setSort(nextSort);

      // 如果有自定义比较函数，使用前端排序，否则触发后端排序
      if (hasComparator) {
        // 前端排序：直接在表格中排序，不调用接口
        api.refreshClientSideRowModel();
      } else {
        // 后端排序：触发排序事件，调用接口
        const sortModel = nextSort ? [{
          columnKey: column.getColId(),
          order: nextSort === 'asc' ? 'ascend' : nextSort === 'desc' ? 'descend' : undefined,
        }] : [];
        onSortChanged && onSortChanged(sortModel, hasComparator);
      }
    };

    return (
      <div
        className="ag-header-cell-label"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isSortable ? 'pointer' : 'default',
          width: '100%'
        }}
        onClick={handleSortClick}
      >
        {isSortable ?
          <Tooltip placement="top" color="#1890ff" title={currentSort === 'asc' ? '点击降序' : currentSort === 'desc' ? '取消排序' : '点击升序'}>
            <span style={{ marginRight: isSortable ? '8px' : '0', color: '#000000', fontWeight: 500 }}>{displayName}</span>
          </Tooltip> :
          <span style={{ color: '#000000', fontWeight: 500 }}>{displayName}</span>}

        {isSortable && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CaretUpOutlined
              style={{
                fontSize: '12px',
                color: currentSort === 'asc' ? '#327aff' : '#bfbfbf',
                marginBottom: '-2px'
              }}
            />
            <CaretDownOutlined
              style={{
                fontSize: '12px',
                color: currentSort === 'desc' ? '#327aff' : '#bfbfbf',
                marginTop: '-2px'
              }}
            />
          </div>
        )}
      </div>
    );
  }, [sortedInfo, onSortChanged]);

  // 保存列顺序
  const saveColumnOrder = (columnApi: any) => {
    try {
      // 获取当前所有列的colId，按当前顺序组成数组
      const columnInfo = columnApi
        .getAllDisplayedColumns()
        .map((col: any) => col.getColId());
      onColumnMoved && onColumnMoved(columnInfo);
    } catch (error) {
      console.error('保存列顺序失败:', error);
    }
  };

  const defaultColDef: ColDef = {
    sortable: false, //禁用列排序
    filter: true, //启用列过滤
    resizable: true, //允许调整列宽
    suppressMenu: true, //控制拖动列换顺序
    cellClass: 'border-cell',
    enablePivot: true,
    headerClass: 'header-bg-color', //为表头添加 CSS 类
    suppressMovable: false, // 默认允许列移动
    flex: 1, // 设置列默认使用flex布局，使列可以自动扩展填充空间
    lockPinned: true, // 固定列不允许换位置
    minWidth: 60, // 设置列的最小宽度，防止过窄
    maxWidth: 1200, // 设置列的最大宽度，防止过宽
  };

  // 处理列配置，确保固定列不可移动，并为可排序列添加自定义表头组件
  const processedColumns = React.useMemo(() => {
    return column?.map((col: any) => {
      // 如果列是固定的，或者已经设置了suppressMovable: true，则确保它不可移动
      const isPinned = col.pinned || col.suppressMovable;

      // 如果列可排序，则使用自定义表头组件
      const hasCustomHeader = col.sortable !== false;

      return {
        ...col,
        suppressMovable: isPinned,
        headerComponent: hasCustomHeader ? CustomHeaderComponent : undefined
      };
    });
  }, [column, CustomHeaderComponent]);

  // 自定义无数据组件，使用Ant Design的Empty组件
  const CustomNoRowsOverlay = () => {
    return (
      <div style={{ padding: '40px 0' }}>
        <Empty description="暂无数据" />
      </div>
    );
  };

  return (
    <>
      <div className={styles.container} style={{ position: 'relative' }}>
        <Spin
          spinning={isLoading}
          style={{
            width: '100%',
            height: '100%',
            paddingTop: '15%',
            background: 'rgb(255, 255, 255,.65)',
            position: 'absolute',
            // top: '30%',
            // left: '50%',
            // right: '50%',
            zIndex: 99,
          }}></Spin>
        {/* 表格区域 - 添加ag-theme-alpine类名作为主题 */}
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            domLayout="normal" // 恢复为normal以保持固定高度设置
            pagination={false}
            headerHeight={headerHeight} //头部高度
            rowHeight={rowHeight} //行高
            columnDefs={processedColumns} //页面列（已处理固定列不可移动）
            rowData={page ? page.items : dataSource} //页面数据
            rowSelection={rowSelection || 'none'} //复选框
            defaultColDef={defaultColDef} //默认配置
            pinnedBottomRowData={pinnedBottomRowData || []} //合计行
            onRowDoubleClicked={onRowDoubleClick} //双击事件
            onCellClicked={onCellClicked}
            getRowStyle={getRowStyle ? (params: any) => getRowStyle(params) as Record<string, any> : undefined}
            rowModelType="clientSide" // 使用客户端排序模型，支持前端排序
            onRowSelected={(event:any) => {
              onRowSelected && onRowSelected(event.node.selected,event.node)
            }}
            onSelectionChanged={(event) => {
              //复选框选择事件
              if (onSelectionChange) {
                // 使用防抖来避免短时间内多次触发
                if (selectionDebounceRef.current) {
                  clearTimeout(selectionDebounceRef.current);
                }
                selectionDebounceRef.current = setTimeout(() => {
                  const selectedNodes = event.api.getSelectedNodes();
                  const selectedRows = selectedNodes.map((node) => node.data);
                  // 获取选中行的ID列表
                  const selectedRowIds = selectedRows.map(row =>
                    getRowId ? getRowId(row) : row[rowKey || 'id']
                  );
                  onSelectionChange(selectedRows, selectedNodes, selectedRowIds);
                }, 50); // 50ms防抖延迟
              }
            }}
            suppressRowClickSelection={suppressRowClickSelection} // 禁用点击整行勾选checkbox功能
            rowMultiSelectWithClick={rowMultiSelectWithClick}
            onColumnVisible={(event) => {
              // 当列的可见性改变时，自动调整列宽以填充空间
              setTimeout(() => {
                event.api.sizeColumnsToFit();
              }, 0);
            }}
            suppressCellSelection={false} // 启用单元格文本选择，允许复制
            // 禁止将列拖出表格区域来隐藏列
            suppressDragLeaveHidesColumns={true}
            // 使用自定义无数据组件
            noRowsOverlayComponent={CustomNoRowsOverlay}
            onColumnResized={(event: any) => {
              onColumnResized && onColumnResized(event)
            }}
            // 处理列移动事件，保存列顺序
            onColumnMoved={(event: any) => {
              if (event.source === 'uiColumnDragged') {
                isColumnMovedRef.current = true
              }
            }}
            onDragStopped={(event: any) => {
              if (isColumnMovedRef.current) {
                isColumnMovedRef.current = false
                saveColumnOrder(event.columnApi)
              }
            }}
            onRowDataUpdated={(params) => {
              // 只在数据更新且不是由选中操作引起的情况下，重新应用选中状态
              selectRowsBasedOnKeys(params);
            }}
          />
        </div>
        {pagination}
        {page && pagination !== false && (
          <Row
            justify='end'
            align='middle'
            style={{
              height: '60px',
              padding: '0 15px',
            }}
          >
            <Pagination
              defaultPageSize={defaultPageSize}
              showQuickJumper={showQuickJumper}
              showSizeChanger={showSizeChanger}
              pageSize={page.size < 0 ? 10 : page.size}
              current={page.current}
              total={page.total}
              pageSizeOptions={
                pageSizeOptions
                  ? pageSizeOptions
                  : ['10', '20', '50', '100', '500']
              }
              onChange={onChangePage}
              className={styles.pagination}
              showTotal={(total) => `共 ${total} 条`}
            />
          </Row>
        )}
      </div>
    </>
  );
};

export default SimpleAgGrid;